// @flow

import React from "react";
import { localize } from "redux-i18n";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  View,
  Text,
  GSGrid,
} from "@components";
import type {
  EmployeeNotification,
  EmployeeRefType,
  NotificationActionType,
} from "@modules/shared/reducer/employee-notifications/types";
import type { EmployeeType } from "@modules/employee/types";
import {
  EMPLOYEE_REF_TYPES,
  NOTIFICATION_ACTION_TYPES,
} from "@modules/shared/constants/notifications";
import SelectEmployee from "../notifications-add-modal/select-employee";

type Props = $Exact<{
  clientId: number,
  actionType: NotificationActionType,
  data: Array<EmployeeNotification>,
  onSubmit: (employee: EmployeeType, successMessage: string) => void,
  onDismissHandler: () => void,
  employeeRefType: EmployeeRefType,
  employeeName: string,
  ...PropsFromLocalize,
}>;

function NotificationActionModal(props: Props) {
  const translationConstants = {
    [NOTIFICATION_ACTION_TYPES.TRANSFER]: {
      single: {
        title:
          props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
            ? "TRANSFER_NOTIFICATION"
            : "TRANSFER_EMPLOYEES",
        successMessage: "TRANSFER_NOTIFICATION_SUCCESS",
      },
      multiple: {
        title:
          props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
            ? "TRANSFER_MULTIPLE_NOTIFICATIONS"
            : "TRANSFER_EMPLOYEES",
        successMessage: "TRANSFER_MULTIPLE_NOTIFICATIONS_SUCCESS",
      },
      subTitle:
        props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
          ? "NOTIFICATION_CONTACTS"
          : "TRANSFERRED_EMPLOYEES",
      subTitle2:
        props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
          ? "SELECT_NEW_EMPLOYEE"
          : "SELECT_NEW_NOTIFICATION_CONTACT",
      subTitleText:
        props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
          ? "NOTIFICATION_CONTACT_TRANSFER_TEXT_RECIPIENT"
          : "NOTIFICATION_CONTACT_TRANSFER_TEXT_TARGET",
    },
    // COPY
    [NOTIFICATION_ACTION_TYPES.COPY]: {
      single: {
        title:
          props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
            ? "COPY_NOTIFICATION"
            : "COPY_NOTIFICATION",
        successMessage: "COPY_NOTIFICATION_SUCCESS",
      },
      multiple: {
        title:
          props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
            ? "COPY_MULTIPLE_NOTIFICATIONS"
            : "COPY_MULTIPLE_NOTIFICATIONS",
        successMessage: "COPY_MULTIPLE_NOTIFICATIONS_SUCCESS",
      },
      subTitle:
        props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
          ? "NOTIFICATION_CONTACTS"
          : "EMPLOYEES",
      subTitle2:
        props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
          ? "SELECT_EMPLOYEE"
          : "SELECT_NOTIFICATION_CONTACT",
      subTitleText:
        props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT
          ? "NOTIFICATION_CONTACT_COPY_TEXT_RECIPIENT"
          : "NOTIFICATION_CONTACT_COPY_TEXT_TARGET",
    },
  };

  function getTranslation(type: string) {
    const data = translationConstants[props.actionType];

    if (type !== "title") {
      if (type === "subTitleText") {
        return props.t(data[type], {
          ownerName: props.employeeName,
        });
      } else {
        return props.t(data[type]);
      }
    }

    const notificationCount = props.data.length;
    if (notificationCount === 0 || notificationCount > 1) {
      return props.t(data.multiple[type], {
        number: notificationCount,
      });
    }

    return props.t(data.single[type]);
  }

  function handleSubmit(employee: EmployeeType) {
    props.onSubmit(employee, getTranslation("successMessage"));
  }

  return (
    <Modal className="modal-large">
      <ModalHeader className="d-flex justify-content-between" toggle={props.onDismissHandler}>
        {getTranslation("title")}
      </ModalHeader>
      <ModalBody className="d-flex flex-column justify-content-start align-content-center">
        <View gap={6}>
          <View gap={2}>
            <Text type="h5" text={getTranslation("subTitle")} />
            <span
              dangerouslySetInnerHTML={{
                __html: getTranslation("subTitleText"),
              }}
            />
            <View width="40%">
              <GSGrid
                columns={[
                  {
                    key: "name",
                    name: props.t("NAME"),
                    align: "left",
                    renderCell: ({ row }: Object) => {
                      return row[props.employeeRefType].name;
                    },
                  },
                  {
                    key: "notificationType",
                    width: 130,
                    name: props.t("NOTIFICATION_TYPE"),
                    renderCell: ({ row }: Object) => {
                      return props.t(row.notificationType);
                    },
                  },
                ]}
                rows={props.data}
              />
            </View>
          </View>
          <View gap={2}>
            <Text type="h5" text={getTranslation("subTitle2")} />
            <SelectEmployee
              clientId={props.clientId}
              editable
              handleSubmit={handleSubmit}
              actionType={props.actionType}
            />
          </View>
        </View>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={props.onDismissHandler}>
          {props.t("CANCEL")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default localize()(NotificationActionModal);
